import api from "../../services/api.service";
import { ActionTree } from "vuex";
import { DispatchState, Guard, PatrolZone, AddMultipleGuardsDTO } from "./types";
import { RootState } from "@/store/types";
import UpdateMultipleGuardsDTO  from "@/types/CloseMultipleEventsDTO";

export const actions: ActionTree<DispatchState, RootState> = {
	async loadGuards({ commit }) {
		const guards = await api.loadGuards();
		commit("setGuards", guards || []);
	},
	async loadAlarmFilters({ commit }) {
		const alarmFilters = await api.loadAlarmQueueFiltersForGuards();
		commit("setAlarmFilters", alarmFilters || []);
	},
	// Loads guard types and sets their icon
	async loadGuardTypes({ commit }) {
		const guardTypes = await api.loadGuardTypes();
		guardTypes.forEach(guardType => {
			guardType.icon =
				"images/" + guardType.dispatchGuardTypeID + "-guard.png";
		});
		commit("setGuardTypes", guardTypes || []);
	},

	async loadGuardsForFilter({ commit }, filterID: number): Promise<void> {
		const guards = await api.loadGuardsForFilter(filterID);
		commit("setGuardsForFilter", guards || []);
	},

	async addNewGuard({ commit }, guard: Guard) {
		guard.guardID = await api.addNewGuard(guard);
		commit("addNewGuard", guard);
	},

	async updateGuard({ commit, getters }, guard: Guard) {
		commit("updateGuard", guard);
		await api.updateGuard(guard);
	},

	async updateMultipleGuards({ commit }, updateDetails: UpdateMultipleGuardsDTO) {
		await api.updateMultipleGuards(updateDetails);
	},

	async deleteGuard({ commit }, guard: Guard) {
		await api.deleteGuard(guard.guardID!);

		commit("deleteGuard", guard);
	},

	async loadPatrolZones({ commit }) {
		const zones = await api.loadPatrolZones();
		if (typeof zones !== "undefined") {
			commit("setPatrolZones", zones);
		}
		else {
			commit("setPatrolZones", null);
		}
	},

	async addPatrolZone({ commit }, zone: PatrolZone) {
		zone.patrolZoneID = await api.addPatrolZone(zone);
		commit("addPatrolZone", zone);
		return zone.patrolZoneID;
	},

	async addGuardToPatrolZone(
		{ dispatch, commit, getters },
		{ patrolZoneID, guardID, lastLocation }
	) {
		const zone = getters.getPatrolZoneByID(patrolZoneID);
		const guard = getters.getGuardByID(guardID);

		const assignedZones = Object.keys(guard.patrolZones) || [];
		assignedZones.push(patrolZoneID);

		commit("addGuardToPatrolZone", zone);
		await api.assignGuardToZone({ patrolZoneID: assignedZones, guardID });

		let guardToUpdate: Guard = { ...getters.getGuardByID(guardID) };
		guardToUpdate.lastLocation = lastLocation;

		await dispatch("updateGuard", guardToUpdate);
		await dispatch("loadGuards");
	},

	async removeGuardFromPatrolZone(
		{ dispatch, commit, getters },
		{ patrolZoneID, guardID }
	) {
		const zone = getters.getPatrolZoneByID(patrolZoneID);

		commit("removeGuardFromPatrolZone", zone);
		await api.removeGuardFromZone({ patrolZoneID, guardID });

		let guardToUpdate: Guard = { ...getters.getGuardByID(guardID) };

		guardToUpdate.lastLocation = null;
		await dispatch("updateGuard", guardToUpdate);
		await dispatch("loadGuards");

	},

	async updateAssignedZones(
		{ dispatch,getters },
		{ guardID, assignedZones, lastLocation }
	) {
		await api.assignGuardToZone({ patrolZoneID: assignedZones, guardID });

		let guardToUpdate: Guard = { ...getters.getGuardByID(guardID) };
		guardToUpdate.lastLocation = lastLocation;

		await dispatch("updateGuard", guardToUpdate);
		await dispatch("loadGuards");

	},

	async updatePatrolZone({ dispatch, commit, getters }, { id, data }) {
		const zone = getters.getPatrolZoneByID(id);
		const shouldUpdateGuards = zone.title !== data.title;
		commit("updatePatrolZone", { zone, data });
		await api.updatePatrolZone(zone);

		dispatch("loadPatrolZones");

		if (shouldUpdateGuards) {
			dispatch("loadGuards");
		}
	},

	async deletePatrolZone({ commit, getters }, id: number) {
		const zone = getters.getPatrolZoneByID(id);
		await api.deletePatrolZone(zone.patrolZoneID);

		commit("deletePatrolZone", zone);
	},

	// Dispatch component's actions
	async updateDispatchStatus({ commit }, eventid: number) {
		const dispatchedGuards = await api.loadDispatchedGuardsForEvent(
			eventid
		);

		if (dispatchedGuards.status === 204) {
			commit("setDispatchedGuards", []);
		} else {
			commit("setDispatchedGuards", dispatchedGuards.data);
		}
	},

	setDispatchedGuards({ commit }, guards: Guard[]) {
		commit("setDispatchedGuards", guards);
	},

	async updateDispatchedGuards({ commit }, { eventId, location, includeGuardsWithMatchingFilter }) {
		const dispatchedGuardsResult = await api.loadDispatchedGuardsForEvent(
			eventId
		);
		const eventGuards: Guard[] = await api.loadGuardsForEvent(
			eventId,
			location,
			includeGuardsWithMatchingFilter
		);
		let dispatchedGuards: Guard[];

		if (dispatchedGuardsResult.status === 204) {
			dispatchedGuards = [];
		} else {
			dispatchedGuards = dispatchedGuardsResult.data;
		}

		commit("updateDispatchedGuards", { eventGuards, dispatchedGuards });
	},

	async dispatchGuard({ commit }, { guardID, eventID, status, location }) {
		const { data } = await api.dispatchGuard({ guardID, eventID, status, location });
		if (data !== null) {
			if (data.success) {
				commit("setDispatchedGuard", { guardID, status });
			}

			return data.success;
		}
		return false;
	},

	async loadGuardsForEvent({ commit }, { eventId, location, includeGuardsWithMatchingFilter = false }): Promise<void> {
		const guards: Guard[] = await api.loadGuardsForEvent(eventId, location, includeGuardsWithMatchingFilter);
		commit("setEventGuards", guards);
	},

	clearGuardsForEvent({ commit }) {
		const guards: Guard[] = [];
		commit("setEventGuards", guards);
	},

	async updateEventLocation({ commit }, location: string) {
		commit("setEventLocation", location);
	},

	filterGuardListByCallsign({ commit }, filterPhrase: string) {
		commit("setGuardsCallsignListFilter", filterPhrase);
	},

	filterGuardListByPatrolZone({ commit }, patrolZoneID: number) {
		commit("setGuardsPatrolZoneListFilter", patrolZoneID);
	}
};
